/**
 * The exact preview: the real document, drawn page by page.
 *
 * pdf.js rasterizes the PDF the server laid out from the actual .docx, so
 * what the panel shows IS the file — page breaks, running header and footer,
 * embedded fonts and colours — not an HTML look-alike of it.
 *
 * Pages appear as soon as there is something to show. The first version of
 * this drew every page into a detached fragment and swapped the whole lot in
 * at the end, so a fourteen-page document showed nothing at all until the
 * fourteenth page had been rasterized (operator report 2026-09-10: "the
 * preview panel is very, very slow"). Now the correctly-sized pages go up
 * immediately and each one is rasterized when it comes near the viewport, so
 * the wait is for ONE page, not for all of them, and scrolling fills in the
 * rest ahead of the scroll.
 *
 * RTL-safe: the pages are images, and the gutter is symmetric.
 */

import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useLanguage } from '@/hooks/useLanguage';

// Vite resolves the worker module URL at build time; pdf.js runs parsing
// and rasterization off the main thread.
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// The version marker is NOT decoration. This file is content-hashed, so its
// URL does not change when the SERVER's answer for it changes — and for a
// day it answered with the wrong MIME type (see deploy/nginx-frontend.conf),
// cached `immutable` for a year. Fixing nginx could not reach a browser that
// already held that answer: it will not even revalidate. Changing the URL is
// the only thing that can, so bump this whenever the served RESPONSE for the
// worker has to be re-fetched.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${PdfWorker}?v=2`;

interface Props {
  /** The PDF bytes (an object URL or ArrayBuffer). */
  data: ArrayBuffer;
  title?: string;
}

/** How far outside the viewport a page starts rasterizing. One screen of
 *  lead time is enough to keep an ordinary scroll ahead of the renderer. */
const RENDER_MARGIN = '100% 0px';

export const PdfPagesView: React.FC<Props> = ({ data, title }) => {
  const { t } = useLanguage();
  const hostRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState<number>(0);
  const tRef = useRef(t);
  tRef.current = t;

  // Track the host width so pages fill the panel and re-render on a real
  // resize. Two guards against the scrollbar feedback loop (pages appear →
  // scrollbar → narrower host → re-render → pages gone → scrollbar gone →
  // wider host → …): a change smaller than a scrollbar is ignored once pages
  // exist, and the change must hold for a moment before it counts.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let timer = 0;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0]?.contentRect.width || 0);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        setWidth((prev) => {
          if (!prev) return w;
          const hasPages = host.querySelector('.artifact-pdf-page-wrap') !== null;
          const threshold = hasPages ? 40 : 4;
          return Math.abs(prev - w) > threshold ? w : prev;
        });
      }, 200);
    });
    ro.observe(host);
    setWidth(Math.floor(host.clientWidth));
    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !width) return undefined;
    let cancelled = false;
    let doc: pdfjsLib.PDFDocumentProxy | null = null;
    let observer: IntersectionObserver | null = null;
    let lazyAvailable = typeof IntersectionObserver === 'function';
    const tasks = new Map<Element, () => Promise<void>>();
    setError(null);

    (async () => {
      try {
        // pdf.js takes ownership of the buffer; hand it a copy so a re-render
        // (resize) can reuse the original.
        doc = await pdfjsLib.getDocument({ data: data.slice(0) }).promise;
        if (cancelled) return;
        const pdf = doc;
        setPageCount(pdf.numPages);

        const gutter = 24;                        // page-to-page gap, like print layout
        const pageWidth = Math.max(200, width - gutter * 2);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        // Rasterize when the page comes near the viewport, once. Until then
        // the page is a correctly-sized white sheet, which is what a page of
        // a document looks like before its ink arrives.
        //
        // Built ON DEMAND — only for a document that HAS a second page — and
        // never allowed to break the preview. A one-page document is the
        // common case and now runs exactly the code that ran before lazy
        // rendering existed. If a browser refuses the observer (its options
        // are the one new platform API on this path), every page is simply
        // drawn as it is created, which is what the previous version did.
        const lazily = (wrap: HTMLElement, draw: () => Promise<void>): boolean => {
          try {
            if (observer === null) {
              observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting) return;
                  const run = tasks.get(entry.target);
                  if (!run) return;
                  tasks.delete(entry.target);
                  observer?.unobserve(entry.target);
                  void run();
                });
              }, { root: host.parentElement, rootMargin: RENDER_MARGIN });
            }
            tasks.set(wrap, draw);
            observer.observe(wrap);
            return true;
          } catch (e) {
            // Once it has failed it will fail again: stop trying, and let
            // every remaining page draw eagerly.
            observer = null;
            lazyAvailable = false;
            return false;
          }
        };

        for (let n = 1; n <= pdf.numPages; n += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const scale = pageWidth / base.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.className = 'artifact-pdf-page';
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.setAttribute(
            'aria-label', tRef.current('chat.artifacts.pageOf', { n, total: pdf.numPages }),
          );
          const wrap = document.createElement('div');
          wrap.className = 'artifact-pdf-page-wrap';
          wrap.appendChild(canvas);

          const draw = async () => {
            if (cancelled) return;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            try {
              await page.render({ canvasContext: ctx, viewport }).promise;
            } catch {
              /* a cancelled render during teardown is not an error */
            }
          };

          if (n === 1) {
            // The first page is what the reader is waiting for: draw it
            // before anything goes on screen, so the panel never flashes an
            // empty sheet where the document should be, and a re-render keeps
            // the previous pages up until this one is ready.
            await draw();
            if (cancelled) return;
            host.replaceChildren(wrap);
          } else {
            // Sized, empty, and in the DOM straight away: the panel reaches
            // its real height while the ink is still arriving.
            host.append(wrap);
            if (!lazyAvailable || !lazily(wrap, draw)) await draw();
          }
        }
      } catch (e: any) {
        // Only ever an error when there is NOTHING on screen. A failure
        // after the first page is drawn used to replace a perfectly good
        // document with "the exact preview could not be drawn" — which is
        // what a user saw on jewishpro.ai (2026-09-10) while the server had
        // built and served the file correctly.
        if (cancelled) return;
        if (host.querySelector('.artifact-pdf-page') === null) {
          setError(e?.message || 'render failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      observer?.disconnect();
      tasks.clear();
      doc?.destroy().catch(() => undefined);
    };
  }, [data, width]);

  return (
    <div className="artifact-pdf" role="document" aria-label={title || 'document'}>
      {error && <div className="artifact-pdf__error">{t('chat.artifacts.exactPreviewFailed')}</div>}
      <div ref={hostRef} className="artifact-pdf__pages" />
      {pageCount !== null && !error && (
        <div className="artifact-pdf__count">{t('chat.artifacts.pageCount', { count: pageCount })}</div>
      )}
    </div>
  );
};

export default PdfPagesView;
